import { Point } from "./Point";
import { Rectangle } from "./Rectangle";
import { Id_finding, Id_category, Id_floor, Id_location } from "./api/Identifiers";
import { Category } from "./Categories";
import { Configuration } from "./Configuration";
import { exists } from "./utils";
import { MapFeature } from "./MapFeature";

let filter_id_category: Id_category = Configuration.filter_finding_id_category;
let is_filtering_on = (filter_id_category !== null);

interface FindingJSON {
    adj_coords: number[];
    adjP1P2: any[];
    category: number;
    coords: number[];
    findingNumber: number;
    has_findingNumber: boolean;
    has_coords: boolean;
    icon: string;
    id_finding: string;   // id_finding is a UUID
    id_floor: number;
    id_location: number;
    location_label: string;
    rawCoords: number[];
    rawP1P2: number[];
}

export class Finding extends MapFeature {
    private _finding_number: number;
    private _has_findnig_number: boolean = false;
    private _icon: string;
    private _id_category: Id_category = null;
    private _id_floor: Id_floor;
    private _id_location: Id_location;
    private _id: Id_finding;
    private _location_label: string;
    private _raw_coord: Point;
    private _raw_geo_ref: Rectangle;
    private _raw: any;

    // MapFeature required methods
    public static readonly MAP_FEATURE_TYPE: string = "finding";
    public get generic_id(): string { return this.id.toString(); }
    public get icon_url(): string { return this._icon; }
    public get raw_coords(): Point|null { return this._raw_coord; }
    public get map_feature_type(): string { return Finding.MAP_FEATURE_TYPE; }

    get id_floor(): Id_floor {
        if (!this._id_floor) {
            return this._id_location;
        }
        return this._id_floor;
    }

    public get id(): Id_finding {
        return this._id;
    }

    public get findingNumber(): number {
        return this._finding_number;
    }

    public get has_findingNumber(): boolean {
        return this._has_findnig_number;
    }

    public get category(): Id_category {
        return this._id_category;
    }

    public get location_label(): string {
        return this._location_label;
    }

    public get raw_geo_ref(): Rectangle|null {
        return this._raw_geo_ref;
    }

    public get id_category(): Id_category {
        return this._id_category;
    }

    public get raw(): any {
        return this._raw;
    }

    public static build_from_raw(raw_finding: any): Finding {

        let r: Finding = new Finding;
        r._location_label = "";
        if (raw_finding.finding) {
            // sometimes the raw finding is buried under an finding field
            raw_finding = raw_finding.finding;
        }
        r._raw = Object.assign({}, raw_finding);

        r._id = Id_finding.wrap(raw_finding.id);
        if (!r._id) {
            r._id = Id_finding.wrap(raw_finding.idRoundFinding);
        }
        if (!r._id) {
            console.debug("finding is missing an id field");
            return null;
        }
        if (!raw_finding.findingNumber) {
            console.debug("finding is missing FindingNumber field");
            r._finding_number = null;
            r._has_findnig_number = false;
        }
        else {
            r._finding_number = raw_finding.findingNumber;
            r._has_findnig_number = true;
        }

        r._id_category = Id_category.wrap(raw_finding.idCategory);
        if (!r._id_category) {
            console.debug("Finding" + r._id.toString() + " is missing required idCategory field.");
            return null;
        }
        if (exists(raw_finding.location)) {
            r._location_label = raw_finding.location;
        }

        if (exists(raw_finding.locationOther)) {
            r._location_label = `${r._location_label} - ${raw_finding.locationOther}`;
        }
        r._id_location = Id_location.wrap(raw_finding.idLocation);
        r._id_floor = Id_floor.wrap(raw_finding.idFloor);
        r._raw_coord = Point.build(raw_finding.xCoord, raw_finding.yCoord);
        r._icon = utilsFindingIcon(raw_finding.icon);

        r._raw_geo_ref = Rectangle.build_from_numbers(raw_finding.p1XCoord,
            raw_finding.p1YCoord,
            raw_finding.p2XCoord,
            raw_finding.p2YCoord);
        return r;
    }

    public get has_coords(): boolean { return exists(this.raw_coords); }

    
    public get toJSON(): FindingJSON {
        return this.as_json;
    }
    public get as_json(): FindingJSON {
        let         raw_p1p2 = this.raw_geo_ref;
        if(!exists(raw_p1p2)){
            raw_p1p2=Rectangle.NULL;
        }
        let         raw_coords = this.raw_coords;
        if(!exists(raw_coords)){
            raw_coords=Point.NULL;
        }
        const r: FindingJSON = {
            adj_coords: this.adj_coords.as_json,
            adjP1P2: this.adj_geo_ref.as_json,
            category: this._id_category.value,
            coords: this.adj_coords.as_json,
            findingNumber: this._finding_number,
            has_findingNumber: this._has_findnig_number,
            has_coords: this.has_coords,
            icon: this._icon,
            id_finding: this._id.value,
            id_floor: this.id_floor.value,
            id_location: this.id_floor.value,  // AWH - reconcile this
            location_label: this._location_label,
            rawCoords: this.raw_coords.as_json,
            rawP1P2: this._raw_geo_ref.as_json,
        };
        return r;
    }

    // public nudge(rhs: Finding): void {
    //     if (this.category === rhs.category) {
    //         // only nudge if the findings are the same category
    //         if (this.id_floor.equals(rhs.id_floor)) {
    //             // only nudge if the findings are on the same floor

    //             if (!this.id.equals(rhs.id)) {
    //                 // only nudge if the ids don't match
    //                 if (this._adj_coord) {
    //                     if (rhs._adj_coord) {
    //                         Nudge(this._adj_coord, rhs._adj_coord, 45, 20);
    //                     }
    //                 }
    //             }
    //         }
    //     }
    // }
}



export class FindingCollection {

    private _findings: any = {};  // {id_finding:Finding}
    private _findings_sorted_by_floor: any = {};   //{(key:string):Id_finding[]} = null;
    private __findings_list: Finding[] = null;  // {id_finding:Finding}
    private __json: any = null;
    private __categories: Set<Id_category> = new Set<Id_category>();
    //private __findings_sorted_by_floor_json: any = null;

    public get categories(): Id_category[] {
        return Array.from(this.__categories);
    }
    public static empty(): FindingCollection { return new FindingCollection(); };


    public constructor() {

    }

    public get count(): number {
        return this.findings.length;
    }
    public get findings(): Finding[] {

        if (!this.__findings_list) {
            this.__findings_list = [];
            Object.keys(this._findings).forEach((id_finding: string) => {
                this.__findings_list.push(this._findings[id_finding]);
            });
        }
        return this.__findings_list;
    }

    public get_finding(id_finding: Id_finding): Finding {
        let r: Finding = this._findings[id_finding.value];
        if (!r) {
            return null;
        }
        return r;
    }


    public get_on_floor(id_floor: Id_floor): FindingCollection {
        let r: FindingCollection = new FindingCollection();
        this.findings.forEach((finding: Finding) => {
            if (finding.id_floor.equals(id_floor)) {
                r.add(finding);
            }
        });
        return r;
    }

    public get_findings_by_category_json(id_category: Id_category): any {
        let r: any = {};

        this.findings.forEach((finding: Finding) => {
            let id_category: string = finding.id_category.toString();
            if (!r[id_category]) {
                r[id_category] = [];
            }
            r[id_category].push(finding.toJSON);
        });
        return r;
    }

    public get as_array(): Finding[] {
        return this.findings;
    }


    public get length(): number {
        return Object.keys(this._findings).length;
    }

    public static build_from_raw(raw_findings: any[]): Finding[] {
        let r: Finding[] = [];
        raw_findings.forEach((raw_finding: any) => {
            let new_finding: Finding = Finding.build_from_raw(raw_finding);

            if (new_finding) {
                r.push(new_finding);
            }
            else {
                console.debug(`null finding created by ${raw_finding}`);
            }
        });
        if (is_filtering_on) {
            r = r.filter((finding: Finding) => {
                return finding.id_category.equals(filter_id_category);
            });
        }


        return r;
    }



    public static wrap(findings: Finding[]): FindingCollection {
        let r: FindingCollection = new FindingCollection();
        findings.forEach((finding: any) => {
            r.add(finding);
        });
        return r;
    }

    private add_findings(findings: Finding[]):FindingCollection {
        findings.forEach((finding: Finding) => this.add(finding));
        return this;
    }

    private add_finding(finding: Finding):FindingCollection {

        this._findings[finding.id.value] = finding;
        let floor_key = finding.id_floor.toString();

        // add the finding to the floor-sorted table
        if (!this._findings_sorted_by_floor[floor_key]) {
            this._findings_sorted_by_floor[floor_key] = [];
        }
        this._findings_sorted_by_floor[finding.id_floor.toString()].push(finding.id);

        this.__json = null;
        this.__findings_list = null;
        this.__categories.add(finding.category);
        return this;
    }

    public add(finding: Finding|Finding[]):FindingCollection {
        if (Array.isArray(finding)){
            return this.add_findings(<Finding[]>finding);
        }
        return this.add_finding(<Finding>finding);
    }

    public get_findings_by_category(category: Category | Id_category): FindingCollection {
        if (category instanceof Category) {
            return FindingCollection.wrap( this.findings.filter((finding: Finding) => finding.id_category.equals((<Category>category).id_category)));
        }
        return FindingCollection.wrap(this.findings.filter((finding: Finding) => finding.id_category.equals(<Id_category>category)));
    }

    public get_findings_by_categories(categories: Id_category[] | Category[]): FindingCollection {
        let retval: FindingCollection = FindingCollection.empty();
        for (var i = 0; i < categories.length; i++) {
            retval = retval.add(this.get_findings_by_category(categories[i]).as_array);
        }
        return retval;
    }

    public get_categories(): string[] {
        let mySet = new Set<string>();
        this.findings.forEach((finding: Finding) => mySet.add(finding.id_category.toString()));
        return Array.from(mySet);
    }

    public toJSON(): any {
        if (this.__json === null) {
            this.__json = JSON.parse(JSON.stringify(Object.keys(this._findings)));
        }
        return this.__json;
    }

    // // assume the finding contains adj_coords
    // private declusterFindings(id_location: Id_location) {
    //     let declusterCore = () => {
    //         this.findings.forEach((lhs: Finding) => {

    //             if (!lhs.id_floor.equals(id_location)) {
    //                 return
    //             }

    //             if (!lhs.has_coords) {
    //                 return;
    //             }
    //             this.findings.forEach((rhs: Finding) => {
    //                 if (!rhs.has_coords) {
    //                     return;
    //                 }
    //                 if (lhs.category == rhs.category) {
    //                     // only perform decluster of findings that are the same category
    //                     if (lhs.id.equals(rhs.id)) {
    //                         // lhs and rhs are the same. ignore
    //                     }
    //                     else {
    //                         lhs.nudge(rhs)
    //                     }
    //                 }
    //             })
    //         })
    //     }

    //     // run the decluster algorithm multiple times
    //     declusterCore();
    //     declusterCore();
    // }


    // returns a JSON object of all findings indexed by floor
    public sorted_by_floor_json(): any {
        let __findings_sorted_by_floor_json: any = {};
        this.findings.forEach((finding: Finding) => {
            let id_location: string = finding.id_floor.toString();
            if (!__findings_sorted_by_floor_json[id_location]) {
                __findings_sorted_by_floor_json[id_location] = [];
            }
            __findings_sorted_by_floor_json[id_location].push(finding.toJSON);
        });
        return __findings_sorted_by_floor_json;
    }
}

function utilsFindingIcon(cms_view_icon: string): string {
    if (!cms_view_icon) {
        return "";
    }
    let icon: string = cms_view_icon;

    if (icon.toLowerCase().substring(0, 4) !== "http") {
        icon = `${Configuration.azureStorageAccountUrl}/cmsview/icons/48/` + icon;
    }
    return icon;
}

